import {
  defaultLangLocale,
  LangLocale,
  type Translated,
  verbatim,
} from '@myre/common-i18n';
import { getLanguagesForCurrentEnv } from '@myre/common-language';
import { compareByLowerCaseFactory } from '@myre/common-primitives';
import { type UiI18n } from '@myre/domain-i18n-wordings';
import { DynamicEnvService } from '@myre/ui-domain-http';
import { DropdownComponent, type ISelectItem } from '@myre/ui-primitive-forms';
import { LayoutColumnComponent } from '@myre/ui-primitive-layouts';

import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { getYear } from 'date-fns';
import { CookieService } from 'ngx-cookie-service';

import { AbstractHelpAnalyticsService } from '../../../analytics/abstract-help-analytics.service';
import { AbstractLanguageAnalyticsService } from '../../../analytics/abstract-language-analytics.service';
import {
  getLangCookie,
  setLangCookie,
} from '../../../cookies/lang-cookie.helpers';
import { WORDINGS } from '../../../myre-wordings-loader';

import { FooterImageComponent } from './footer-items/footer-image/footer-image.component';
import { FooterTextComponent } from './footer-items/footer-text/footer-text.component';
import { FooterUrlComponent } from './footer-items/footer-url/footer-url.component';
import { getFooterLinks } from './footer-links';

export interface IFooterLinks {
  header: Translated;
  items: IFooterItem[];
}

export interface IFooterItem {
  name: Translated;
  /** An absolute URL, or a router-link path segments array. */
  url: string | string[];
  isActive: boolean;
  /** The one item pointing at the help app, singled out for reporting. */
  isUserGuide?: boolean;
  authRequired?: boolean;
  target?: '_self' | '_blank' | '_parent' | '_top';
}

@Component({
  selector: 'myre-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    FooterImageComponent,
    FooterTextComponent,
    FooterUrlComponent,
    FormsModule,
    LayoutColumnComponent,
  ],
})
export class FooterComponent implements OnInit {
  @Input()
  public logoPath: string;

  @Input()
  public cookieSuffix: string;

  @Input()
  public domain: string;

  @Input()
  public set isAuth(isAuth: boolean) {
    this._isAuth = isAuth;

    this._filterLinks();
  }

  public get isAuth(): boolean {
    return this._isAuth;
  }

  private readonly _cookieService = inject(CookieService);
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  protected readonly _wordings = inject<UiI18n>(WORDINGS);
  private readonly _abstractHelpAnalyticsService = inject(
    AbstractHelpAnalyticsService,
    {
      optional: true,
    },
  );

  private readonly _abstractLanguageAnalyticsService = inject(
    AbstractLanguageAnalyticsService,
    {
      optional: true,
    },
  );

  private _isAuth: boolean;

  protected _links: IFooterLinks[];
  protected _selectedLang: LangLocale;
  protected _languageList: ISelectItem<LangLocale>[] =
    getLanguagesForCurrentEnv()
      .map(l => ({
        value: l.langLocale,
        label: verbatim(l.nativeName),
      }))
      .sort(compareByLowerCaseFactory('label'));

  protected _copyright = this._wordings._ui.footer.copyright({
    bindings: { year: getYear(new Date()) },
  });

  public ngOnInit(): void {
    this._selectedLang = this._getSelectedLang();
    this._filterLinks();
  }

  protected _onItemClicked(item: IFooterItem): void {
    if (item.isUserGuide) {
      this._abstractHelpAnalyticsService?.trackUserGuideOpened({
        source: 'footer',
      });
    }
  }

  protected _changeLanguage(lang: LangLocale): void {
    this._abstractLanguageAnalyticsService?.trackLanguageChanged({
      language: lang,
    });
    this._setCookie(lang);
    location.reload();
  }

  private _filterLinks(): void {
    this._links = getFooterLinks(
      this._dynamicEnvService.env.myre.help.front.host,
      this._wordings._ui.footer,
    ).map(list => ({
      header: list.header,
      items: list.items.filter(
        link => link.isActive && (!link.authRequired || this._isAuth),
      ),
    }));
  }

  private _getSelectedLang(): LangLocale {
    return (
      getLangCookie({
        cookieService: this._cookieService,
        cookieSuffix: this.cookieSuffix,
      }) || defaultLangLocale
    );
  }

  private _setCookie(lang: string): void {
    setLangCookie({
      lang,
      cookieService: this._cookieService,
      cookieSuffix: this.cookieSuffix,
      domain: this.domain,
    });
  }
}
